import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ShareButtons from './ShareButtons';

export default function Lightbox({ items, index, onClose, onPrev, onNext }) {
  const item = items[index];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="relative max-w-6xl w-[92vw] md:w-[86vw] lg:w-[80vw] max-h-[92vh] flex flex-col gap-4"
          initial={{ scale: 0.96, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.96, y: 10 }}
          transition={{ type: 'spring', stiffness: 180, damping: 18 }}
          onClick={(e) => e.stopPropagation()}
        >
          {item.type === 'video' ? (
            item.videoUrl ? (
              <video
                src={item.videoUrl}
                poster={item.posterUrl || item.imageUrl}
                className="w-full h-auto max-h-[75vh] md:max-h-[80vh] lg:max-h-[84vh] rounded-xl border border-white/10 shadow-2xl"
                controls
                autoPlay
                playsInline
              />
            ) : item.embedUrl ? (
              <div className="relative w-full aspect-video max-h-[75vh] md:max-h-[80vh] lg:max-h-[84vh] rounded-xl overflow-hidden border border-white/10 shadow-2xl">
                <iframe
                  title={item.title}
                  src={item.embedUrl}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : (
              <img
                src={item.posterUrl || item.imageUrl}
                alt={item.title}
                className="w-full h-auto max-h-[75vh] md:max-h-[80vh] lg:max-h-[84vh] object-contain rounded-xl border border-white/10 shadow-2xl"
              />
            )
          ) : (
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-auto max-h-[75vh] md:max-h-[80vh] lg:max-h-[84vh] object-contain rounded-xl border border-white/10 shadow-2xl"
            />
          )}
          <div className="absolute top-3 right-3 flex gap-2">
            <button
              type="button"
              onClick={onPrev}
              className="px-3 py-2 rounded-md bg-white/10 text-white hover:bg-white/20 border border-white/20"
              aria-label="Previous"
            >
              ←
            </button>
            <button
              type="button"
              onClick={onNext}
              className="px-3 py-2 rounded-md bg-white/10 text-white hover:bg-white/20 border border-white/20"
              aria-label="Next"
            >
              →
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-md bg-red-500/70 text-white hover:bg-red-500 border border-white/20"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="mt-0 px-2">
            <h3 className="text-white text-xl font-bold">{item.title}</h3>
            <p className="text-white/80 text-sm mt-1">{new Date(item.date).toLocaleDateString()}</p>
            <p className="text-white/80 text-sm mt-2">{item.description}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {item.tags.map((t) => (
                <span key={t} className="text-[11px] px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-200 border border-cyan-400/20">
                  {t}
                </span>
              ))}
            </div>
            <div className="mt-4">
              <ShareButtons
                url={`${window.location.origin}/gallery?id=${item.id}`}
                title={item.title}
                text={item.description}
              />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
